/**
 * MapTemplateBrowserModal
 *
 * Modal for browsing, saving, and applying map templates.
 * Visual design matches PageBrowserModal pattern.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Search,
  Save,
  Copy,
  Download,
  MoreVertical,
  Pencil,
  Trash2,
  MapPin,
  Loader2,
  Check,
  ArrowUpDown,
} from 'lucide-react';
import { FOREST_GREEN, BORDER, WHITE } from '@/styles/tokens';
import type { MapTemplate } from '@/types/mapTemplate';
import type { useMapTemplateBrowser, SortField } from '@/hooks/editor/useMapTemplateBrowser';

type MapTemplateBrowserReturn = ReturnType<typeof useMapTemplateBrowser>;

interface MapTemplateBrowserModalProps {
  browser: MapTemplateBrowserReturn;
}

const SORT_OPTIONS: { label: string; value: SortField }[] = [
  { label: 'Recently Updated', value: 'updatedAt' },
  { label: 'Name A-Z', value: 'name' },
  { label: 'Date Created', value: 'createdAt' },
];

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 30) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function getPinCount(config: Record<string, unknown>): number {
  const pins = config.pins as unknown[] | undefined;
  return Array.isArray(pins) ? pins.length : 0;
}

function getGroupCount(config: Record<string, unknown>): number {
  const groups = config.groups as unknown[] | undefined;
  return Array.isArray(groups) ? groups.length : 0;
}

// ============================================================================
// Template Row Component
// ============================================================================

interface TemplateRowProps {
  template: MapTemplate;
  isSelected: boolean;
  isLinked: boolean;
  onSelect: () => void;
  onRename: (newName: string) => void;
  onDelete: () => void;
}

const TemplateRow: React.FC<TemplateRowProps> = ({
  template,
  isSelected,
  isLinked,
  onSelect,
  onRename,
  onDelete,
}) => {
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState(template.name);
  const renameInputRef = useRef<HTMLInputElement>(null);

  const pinCount = getPinCount(template.configuration);
  const groupCount = getGroupCount(template.configuration);

  useEffect(() => {
    if (isRenaming && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [isRenaming]);

  const handleRenameSubmit = () => {
    if (renameValue.trim() && renameValue !== template.name) {
      onRename(renameValue.trim());
    }
    setIsRenaming(false);
  };

  return (
    <div
      onClick={onSelect}
      className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer transition-colors ${
        isSelected ? 'bg-emerald-50/50' : 'hover:bg-gray-50'
      }`}
    >
      {/* Icon */}
      <span className="text-base shrink-0" role="img">🗺️</span>

      {/* Name + metadata */}
      <div className="flex-1 min-w-0">
        {isRenaming ? (
          <div className="flex items-center gap-1">
            <input
              ref={renameInputRef}
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleRenameSubmit();
                if (e.key === 'Escape') setIsRenaming(false);
              }}
              onBlur={handleRenameSubmit}
              onClick={(e) => e.stopPropagation()}
              className="w-full rounded border border-emerald-300 px-1.5 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
          </div>
        ) : (
          <>
            <div className="flex items-center gap-1.5">
              <span
                className={`text-sm truncate ${
                  isSelected ? 'font-semibold text-emerald-700' : 'font-medium text-gray-900'
                }`}
              >
                {template.name}
              </span>
              {isLinked && (
                <span className="text-[9px] px-1 py-0.5 rounded bg-emerald-100 text-emerald-700 shrink-0 font-medium">
                  linked
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              {template.description && (
                <span className="text-[11px] text-gray-400 truncate max-w-[200px]">
                  {template.description}
                </span>
              )}
              {pinCount > 0 && (
                <span className="text-[10px] text-gray-400 flex items-center gap-0.5 shrink-0">
                  <MapPin className="h-2.5 w-2.5" />
                  {pinCount} pin{pinCount !== 1 ? 's' : ''}
                </span>
              )}
              {groupCount > 0 && (
                <span className="text-[10px] text-gray-400 shrink-0">
                  {groupCount} group{groupCount !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          </>
        )}
      </div>

      {/* Relative time */}
      <span className="text-[10px] text-gray-400 shrink-0 w-14 text-right">
        {formatRelativeTime(template.updatedAt)}
      </span>

      {/* Context menu */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            onClick={(e) => e.stopPropagation()}
            className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 shrink-0"
          >
            <MoreVertical className="h-3.5 w-3.5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-32">
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              setRenameValue(template.name);
              setIsRenaming(true);
            }}
            className="text-xs"
          >
            <Pencil className="h-3 w-3 mr-1.5" />
            Rename
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="text-xs text-red-600 focus:text-red-600"
          >
            <Trash2 className="h-3 w-3 mr-1.5" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

// ============================================================================
// Main Modal Component
// ============================================================================

export const MapTemplateBrowserModal: React.FC<MapTemplateBrowserModalProps> = ({
  browser,
}) => {
  const {
    isOpen,
    closeModal,
    templates,
    isLoading,
    error,
    linkedTemplateId,
    searchQuery,
    setSearchQuery,
    sortBy,
    setSortBy,
    selectedTemplateId,
    setSelectedTemplateId,
    showSaveAsForm,
    setShowSaveAsForm,
    saveAsName,
    setSaveAsName,
    saveAsDescription,
    setSaveAsDescription,
    saveTemplate,
    saveAsTemplate,
    applyTemplate,
    deleteTemplate,
    renameTemplate,
    isSaving,
  } = browser;

  const searchInputRef = useRef<HTMLInputElement>(null);
  const saveAsNameRef = useRef<HTMLInputElement>(null);

  const canSave = !!linkedTemplateId;
  const canApply = !!selectedTemplateId;

  // Auto-focus search on open
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      const t = setTimeout(() => searchInputRef.current?.focus(), 100);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [isOpen]);

  // Auto-focus Save As name input
  useEffect(() => {
    if (showSaveAsForm && saveAsNameRef.current) {
      saveAsNameRef.current.focus();
    }
  }, [showSaveAsForm]);

  const handleSaveAs = () => {
    if (saveAsName.trim()) {
      saveAsTemplate(saveAsName.trim(), saveAsDescription.trim() || undefined);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) closeModal(); }}>
      <DialogContent variant="light" className="sm:max-w-3xl p-0 gap-0">
        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-4 pb-2">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Map Templates</h2>
            <p className="text-[11px] text-gray-500">
              {templates.length} template{templates.length !== 1 ? 's' : ''} in project
            </p>
          </div>
        </div>

        {/* Search + Sort bar */}
        <div className="flex items-center gap-2 px-4 pb-3 border-b border-gray-100">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-md border border-gray-200 py-1.5 pl-8 pr-3 text-xs placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1.5 text-xs text-gray-600 hover:bg-gray-50 shrink-0">
                <ArrowUpDown className="h-3 w-3" />
                Sort
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-36">
              {SORT_OPTIONS.map((opt) => (
                <DropdownMenuItem
                  key={opt.value}
                  onClick={() => setSortBy(opt.value)}
                  className="text-xs"
                >
                  {opt.label}
                  {sortBy === opt.value && (
                    <Check className="ml-auto h-3 w-3" />
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Error message */}
        {error && (
          <div className="mx-4 mt-2 px-3 py-2 rounded-md bg-red-50 border border-red-200 text-xs text-red-600">
            {error}
          </div>
        )}

        {/* Template List */}
        <div className="flex-1 overflow-y-auto" style={{ height: '50vh' }}>
          {isLoading ? (
            <div className="flex items-center justify-center py-16 gap-2 text-gray-400">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-xs">Loading templates...</span>
            </div>
          ) : templates.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <MapPin className="h-8 w-8 text-gray-300" />
              <p className="text-xs text-gray-500">
                {searchQuery ? 'No templates match your search' : 'No map templates yet'}
              </p>
              <p className="text-[11px] text-gray-400">
                Save your current map configuration as a template to reuse it
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {templates.map((template) => (
                <TemplateRow
                  key={template._id}
                  template={template}
                  isSelected={selectedTemplateId === template._id}
                  isLinked={linkedTemplateId === template._id}
                  onSelect={() => setSelectedTemplateId(template._id)}
                  onRename={(newName) => renameTemplate(template._id, newName)}
                  onDelete={() => deleteTemplate(template._id)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Save As Form (inline) */}
        {showSaveAsForm && (
          <div className="mx-4 mb-2 p-3 bg-gray-50 rounded-md border border-gray-100">
            <p className="text-xs font-medium text-gray-900 mb-2">Save as new template</p>
            <div className="flex flex-col gap-2">
              <input
                ref={saveAsNameRef}
                placeholder="Template name"
                value={saveAsName}
                onChange={(e) => setSaveAsName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && saveAsName.trim()) handleSaveAs();
                  if (e.key === 'Escape') setShowSaveAsForm(false);
                }}
                className="w-full rounded-md border border-gray-200 py-1.5 px-3 text-xs placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
              />
              <input
                placeholder="Description (optional)"
                value={saveAsDescription}
                onChange={(e) => setSaveAsDescription(e.target.value)}
                className="w-full rounded-md border border-gray-200 py-1.5 px-3 text-xs placeholder:text-gray-400 focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
              />
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setShowSaveAsForm(false)}
                  className="rounded-md border border-gray-200 px-2.5 py-1 text-xs text-gray-600 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  disabled={!saveAsName.trim() || isSaving}
                  onClick={handleSaveAs}
                  className="rounded-md px-2.5 py-1 text-xs text-white disabled:opacity-50"
                  style={{ backgroundColor: FOREST_GREEN.DEFAULT }}
                >
                  {isSaving ? (
                    <Loader2 className="h-3 w-3 animate-spin inline mr-1" />
                  ) : (
                    <Check className="h-3 w-3 inline mr-1" />
                  )}
                  Create
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Footer Actions */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
          <div className="flex gap-2">
            <button
              disabled={!canSave || isSaving}
              onClick={saveTemplate}
              title={canSave ? 'Update the linked template with current configuration' : 'No template linked to this map'}
              className="flex items-center gap-1.5 rounded-md border border-gray-200 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Save className="h-3.5 w-3.5" />
              )}
              Save
            </button>
            <button
              disabled={isSaving}
              onClick={() => setShowSaveAsForm(true)}
              className="flex items-center gap-1.5 rounded-md border border-gray-200 px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Copy className="h-3.5 w-3.5" />
              Save As
            </button>
          </div>
          <button
            disabled={!canApply || isSaving}
            onClick={() => selectedTemplateId && applyTemplate(selectedTemplateId)}
            className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs text-white disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ backgroundColor: canApply ? FOREST_GREEN.DEFAULT : BORDER.DEFAULT }}
          >
            <Download className="h-3.5 w-3.5" />
            Apply
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
